import {
  ArrowRotateLeftIcon,
  AreaViewIcon,
  makeStyles,
  SurfaceViewIcon,
  Typography,
  useTranslation
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { getObjectLabels, getWorkOrderHiddenItems, restoreWorkOrderCard } from '@paintscout/util/builder'
import type { RenderableItem, WorkOrderLayoutView } from '@paintscout/util/builder'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import React from 'react'
import { useQuote } from '../context'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        border: `1px solid ${neutral.P85}`,
        // `xl` (12px) — closest token to the mock's 10px panel, same as the cards.
        borderRadius: theme.borderRadius.xl,
        overflow: 'hidden'
      },
      // Header bar naming the section. The nav bar's "Hidden items" button controls whether this is on
      // screen at all, so there's no collapse control here.
      header: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minHeight: 44,
        padding: theme.spacing(1, 2),
        backgroundColor: neutral.P94,
        color: neutral.P35,
        fontSize: theme.typography.pxToRem(15),
        fontWeight: 500
      },
      headerIcon: {
        fontSize: 18,
        color: neutral.P60
      },
      body: {
        backgroundColor: neutral.White,
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2)
      },
      // One block per view: its label, then its cards. No rules between rows — the gaps carry it.
      group: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2)
      },
      groupLabelRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
      },
      groupIcon: {
        fontSize: 18,
        color: neutral.P60
      },
      groupLabel: {
        color: neutral.P60,
        fontSize: theme.typography.pxToRem(16)
      },
      row: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: theme.spacing(2)
      },
      name: {
        color: neutral.P35,
        fontSize: theme.typography.pxToRem(16),
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      restoreButton: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        border: 'none',
        background: 'none',
        padding: 0,
        font: 'inherit',
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P50,
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'opacity 120ms ease',
        '&:hover': { opacity: 0.75 }
      },
      restoreIcon: {
        fontSize: 16
      }
    }
  },
  { name: 'WorkOrderHiddenItemsNext' }
)

/** Resolve an item name that may be a plain string or an overridable {useCustom,custom,default}. */
function resolveItemName(item: RenderableItem): string {
  const { name } = item
  if (!name) {
    return ''
  }
  if (typeof name === 'string') {
    return name
  }
  return (name.useCustom ? name.custom : name.default) ?? ''
}

/**
 * The "Hidden items (N)" section for the Work Order view: every hidden card across both views,
 * grouped under Areas / Surfaces, each with a Restore action. The view renders it above the cards
 * while the nav bar's Hidden items button is toggled on. Null when nothing is hidden.
 */
export default function WorkOrderHiddenItemsNext() {
  const classes = useStyles()
  const { quote, updateQuote } = useQuote()
  const { options } = useClientOptions()
  const { __, lang } = useTranslation()
  const objectLabels = getObjectLabels({ options })

  const hidden = getWorkOrderHiddenItems({ quote, options, lang })
  if (hidden.total === 0) {
    return null
  }

  const handleRestore = (view: WorkOrderLayoutView, key: string) => {
    updateQuote({ quote: restoreWorkOrderCard({ quote, view, key }) })
  }

  const groups: {
    view: WorkOrderLayoutView
    label: string
    Icon: React.ComponentType<{ className?: string }>
    items: RenderableItem[]
  }[] = [
    { view: 'area', label: __('Areas'), Icon: AreaViewIcon, items: hidden.area },
    { view: 'substrate', label: __(objectLabels.substrate.plural), Icon: SurfaceViewIcon, items: hidden.substrate }
  ]

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <VisibilityOffIcon className={classes.headerIcon} />
        <span>{`${__('Hidden items')} (${hidden.total})`}</span>
      </div>
      <div className={classes.body}>
        {groups
          .filter((group) => group.items.length > 0)
          .map((group) => (
            <div className={classes.group} key={group.view}>
              <div className={classes.groupLabelRow}>
                <group.Icon className={classes.groupIcon} />
                <Typography className={classes.groupLabel}>{group.label}</Typography>
              </div>
              {group.items.map((item) => (
                <div className={classes.row} key={`${group.view}:${item.key}`}>
                  {/* Name + dimensions, matching how the card itself is titled. */}
                  <Typography className={classes.name}>
                    {item.subtitle ? `${resolveItemName(item)} (${item.subtitle})` : resolveItemName(item)}
                  </Typography>
                  <button
                    type="button"
                    className={classes.restoreButton}
                    onClick={() => handleRestore(group.view, item.key)}
                  >
                    <ArrowRotateLeftIcon className={classes.restoreIcon} />
                    {__('Restore')}
                  </button>
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  )
}
